import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  styleMap,
} from '@lit'

import styles from '@nova/styles'
import { formatMoney, handleize } from '@nova/utils'

import { addToCart } from '@nova/utils'

const quickShopState = {
  initial: 'SHOW_OPTION_SELECTOR',
  submit: 'SHOW_ADD_TO_CART',
  loading: 'SHOW_LOADING',
  confirm: 'SHOW_CONFIRMATION',
  error: 'ERROR',
}

class NovaProductCard extends LitElement {
  static properties = {
    url: {
      type: String,
    },
    available: {
      type: Boolean,
    },
    title: {
      type: String,
    },
    subtitle: {
      type: String,
    },
    primaryImage: {
      type: String,
      attribute: 'has-primary-image',
    },
    secondaryImage: {
      type: String,
      attribute: 'has-secondary-image',
    },
    compareAtPrice: {
      type: String,
      attribute: 'compare-at-price',
    },
    savingsPercent: {
      type: Number,
      attribute: 'savings-percent',
    },
    swatches: {
      type: Array,
    },
    sizes: {
      type: Array,
    },
    price: {
      type: String,
    },
    vendor: {
      type: String,
    },
    sku: {
      type: String,
    },
    variants: {
      type: Array,
    },
    badges: {
      type: Array,
    },
    activeIndex: {
      type: Number,
    },
    options: {
      type: Array,
    },
    hideRatings: {
      attribute: 'hide-ratings',
    },
    hideSwatches: {
      state: true,
    },
    isHovered: {
      state: true,
    },
    isMobile: {
      state: true,
    },
    slidesForCarousel: {
      type: Number,
      state: true,
    },
    quickShopVariant: {
      type: Object,
      state: true,
    },
    status: {
      type: String,
      state: true,
    },
    swatchVariants: {
      type: Array,
    },
    mode: {
      type: String,
    },
  }

  $productCardContentRef = createRef()
  $swatchHoverImage = createRef()
  $primaryImage = createRef()

  get activeTitle() {
    return this.swatchVariants[this.activeIndex]?.swatchTitle || false
  }

  get activeId() {
    return this.swatchVariants[this.activeIndex]?.id || this.id
  }

  get activePrice() {
    if (this.quickShopVariant) return formatMoney(this.quickShopVariant.price)

    if (!this.swatchVariants.length) return formatMoney(this.price)

    return formatMoney(this.swatchVariants[this.activeIndex]?.price)
  }

  get activeAvailable() {
    if (this.quickShopVariant) return this.quickShopVariant.available

    if (this.swatchVariants[this.activeIndex]) {
      return this.swatchVariants[this.activeIndex]?.available
    }
    return this.available
  }

  get activeImage() {
    let image =
      this.swatchVariants[this.activeIndex]?.featured_image?.src ||
      this.primaryImage ||
      ''

    const isSmall = image
      .split('_')
      .some(
        (fragment) => fragment.includes('x480') || fragment.includes('480x'),
      )

    if (!isSmall) {
      const fragments = image.split('.')
      fragments[fragments.length - 2] = `${fragments[fragments.length - 2]
        }_480x`
      image = fragments.join('.')
    }

    return `${image}&width=480&height=640`
  }

  get activeUrl() {
    const variantId = this.quickShopVariant
      ? this.quickShopVariant.id
      : this.swatchVariants[this.activeIndex]?.id
    return variantId ? `${this.url}?variant=${variantId}` : this.url
  }

  get allVariantsBySwatch() {
    const swatch = this.swatchVariants[this.activeIndex]?.swatchTitle

    return this.variants.filter((variant) => variant.options?.includes(swatch))
  }

  parseCents(value) {
    if (value === null || value === undefined || value === '') return 0

    const normalized =
      typeof value === 'string' ? value.replace('.', '') : value
    const cents = Number.parseFloat(normalized)

    return Number.isFinite(cents) ? cents : 0
  }

  get activePriceCents() {
    if (this.quickShopVariant) {
      return this.parseCents(this.quickShopVariant.price)
    }

    if (this.swatchVariants.length && this.swatchVariants[this.activeIndex]) {
      return this.parseCents(this.swatchVariants[this.activeIndex].price)
    }

    return this.parseCents(this.price)
  }

  get activeCompareAtCents() {
    const price = this.activePriceCents
    let compareAt = 0

    if (this.quickShopVariant) {
      compareAt = this.parseCents(this.quickShopVariant.compare_at_price)
    } else if (
      this.swatchVariants.length &&
      this.swatchVariants[this.activeIndex]
    ) {
      compareAt = this.parseCents(
        this.swatchVariants[this.activeIndex].compare_at_price,
      )
    }

    if (!compareAt && this.compareAtPrice) {
      compareAt = this.parseCents(this.compareAtPrice)
    }

    if (!compareAt) {
      compareAt = this.parseCents(this.variants?.[0]?.compare_at_price)
    }

    return compareAt > price ? compareAt : 0
  }

  get activeCompareAtPrice() {
    const compareAt = this.activeCompareAtCents

    if (compareAt > 0) {
      return formatMoney(compareAt)
    }

    return false
  }

  get priceSaving() {
    const compareAt = this.activeCompareAtCents
    const price = this.activePriceCents

    if (compareAt > price) {
      return Math.round(((compareAt - price) * 100) / compareAt)
    }

    if (this.compareAtPrice && this.savingsPercent > 0) {
      return this.savingsPercent
    }

    return 0
  }

  constructor() {
    super()
    this.activeIndex = 0
    this.variants = []
    this.swatches = []
    this.availableSwatches = []
    this.options = []
    this.swatchVariants = []
    this.swatchesPosition = 0
    this.swatchesPerSlide = 5
    this.activeSwatchPage = 0
    this.slidesForCarousel = 1
    this.isHovered = false
    this.isMobile = false
    this.status = quickShopState.initial
    this.hideRatings = window.Shopify.shop.includes('wholesale') // Hide ratings by default on wholesale store
    this.mode = ''
  }

  connectedCallback() {
    super.connectedCallback()
    window.addEventListener('nova:resize-width', this.handleResize)
  }

  disconnectedCallback() {
    window.removeEventListener('nova:resize-width', this.handleResize)
  }

  async firstUpdated() {
    this.setSwatchVariants()
    if (this.options?.length) {
      this.setActiveByVariantId(this.id, true)
    }

    this.handleResize()
  }

  updated(changedProperties) {
    if (
      changedProperties.has('variants') ||
      changedProperties.has('swatches')
    ) {
      this.setSwatchVariants()
    }
    if (changedProperties.has('activeIndex')) {
      this.requestUpdate()
    }

    if (changedProperties.has('activeImage')) {
      this.requestUpdate()
    }
  }

  handleOptionSelection(event) {
    this.status = quickShopState.submit
    this.quickShopVariant = event.detail.variant
  }

  setSwatchVariants() {
    if (!this.swatches || !this.variants.length) return []
    let nonColorOptions
    if (this.options?.length) {
      nonColorOptions = this.options.filter(
        (option) => !this.swatches.includes(option),
      )
    }

    const matches = this.swatches.reduce((accumulator, swatch) => {
      let match
      if (this.options?.length) {
        match = this.variants?.find((variant) => {
          for (const option of nonColorOptions) {
            if (!variant.options.includes(option)) return false
          }
          return variant.options.includes(swatch)
        })
      } else {
        match = this.variants?.find((variant) =>
          variant.options.includes(swatch),
        )
      }

      if (match) {
        const swatchHandle = handleize(swatch)
        const swatchColorConfig = window.__NOVA_GLOBAL__?.swatches[swatchHandle]
        match.swatchTitle = swatch
        match.swatchHandle = swatchHandle
        match.swatchBackground1 = 'transparent'
        match.swatchBackground2 = 'transparent'
        if (swatchColorConfig) {
          if (swatchColorConfig[0]) {
            match.swatchBackground1 = swatchColorConfig[0]
          }
          if (swatchColorConfig[1]) {
            match.swatchBackground2 = swatchColorConfig[1]
          }
        }
        accumulator.push(match)
      }
      return accumulator
    }, [])
    this.swatchVariants = matches
    this.availableSwatches = this.swatchVariants.map(
      (swatchVariant) => swatchVariant.swatchTitle,
    )
    this.requestUpdate()
  }

  setSlidesForCarousel() {
    const container = this.$productCardContentRef.value

    if (this.isMobile || !container) {
      this.slidesForCarousel = 1
      return
    }

    const styles = getComputedStyle(container)
    const totalWidth =
      container.clientWidth -
      Number.parseFloat(styles.paddingLeft) -
      Number.parseFloat(styles.paddingRight)
    // Swatch size: var(--nova-product-color-swatch-size) = 25px over 768px screen width
    // Swatch gap: var(--nova-space-xs) = 10px over 768px screen width
    const slidesToShow = Math.floor(totalWidth / (25 + 10))
    this.slidesForCarousel = slidesToShow >= 1 ? slidesToShow : 1
  }

  handleSwatchClick(event) {
    const { variantId } = event.target.dataset
    this.quickShopVariant = null

    if (!variantId) return

    this.status = quickShopState.initial
    this.setActiveByVariantId(variantId)
    this.handleSelectedVariantImage(event)
  }

  handleSelectedVariantImage(event) {
    const imageUrl = event.target.getAttribute('data-variant-image');
    if (!imageUrl) return;

    const slot = this.$primaryImage.value;
    if (!slot) return;

    const assigned = slot.assignedElements({ flatten: true });
    const wrapper = assigned[0];
    if (!wrapper) return;

    const img = wrapper.querySelector('img');
    if (!img) return;

    const widths = [
      246, 320, 400, 480, 560, 640, 700, 768,
      836, 904, 1024, 1280, 1366, 1440, 1600, 1920, 2560
    ];


    function generateSrcset(url, widths) {
      const base = new URL(`https:${url}`);
      return widths
        .map(width => {
          const clone = new URL(base);
          clone.searchParams.set('width', width);
          return `${clone.toString()} ${width}w`;
        })
        .join(', ');
    }

    img.srcset = generateSrcset(imageUrl, widths);
    img.src = imageUrl;
  }

  handleResize = () => {
    this.isMobile = window.innerWidth <= 768
    this.setSlidesForCarousel()
  }

  handleMouseEnter() {
    if (window.innerWidth <= 750) return
    this.isHovered = true
  }

  handleMouseLeave() {
    this.isHovered = false
  }

  setActiveIndex(index) {
    this.activeIndex = index
  }

  setActiveByVariantId(id, setPage = false) {
    const variantIndex = this.swatchVariants.findIndex(
      (variant) => Number.parseInt(variant.id) === Number.parseInt(id),
    )

    if (variantIndex > -1) {
      this.setActiveIndex(variantIndex)
    }

    if (setPage) {
      const pageNumber = this.paginatedSwatches.findIndex((page) =>
        page.some((variant) => variant.id === Number.parseInt(id)),
      )
      if (pageNumber > -1 && pageNumber !== this.activeSwatchPage) {
        this.setSwatchPage(pageNumber)
      }
    }
  }

  getSwatchesForDisplay() {
    if (!this.availableSwatches?.length) return []
    if (this.mode !== 'predictive-search') return this.availableSwatches
    const max = Number(window.predictiveSearchSettings?.maxVisibleSwatches)
    const cap = Number.isFinite(max) && max > 0 ? max : 6
    return this.availableSwatches.slice(0, cap)
  }

  findVariantBySwatch(swatch) {
    return this.variants.find((variant) => variant.options.includes(swatch))
  }

  checkColorSwatchUnavailable(swatch) {
    return !this.variants.some(
      (variant) => variant.options.includes(swatch) && variant.available,
    )
  }

  sanitizeString(string) {
    return string
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
  }

  async handleAddToCartClick(e) {
    this.status = quickShopState.loading
    try {
      const variantId = e.target.dataset.variantId
      await addToCart({ id: variantId })
      this.status = quickShopState.confirm
    } catch (error) {
      this.status = quickShopState.error
    } finally {
      const quickShop = this.shadowRoot.querySelector(
        'nova-product-card-quick-shop',
      )
      if (quickShop) {
        quickShop.setActiveVariant(null)
      }
      setTimeout(() => {
        this.status = quickShopState.initial
      }, 2000)
    }
  }

  renderMedia() {
    return html`
      <div class="product-card__media ${classMap({
      'product-card__media--enable-hover': this.secondaryImage,
      'mode__predictive-search': this.mode === 'predictive-search',
    })}">
        <div class="product-card__image product-card__image--primary">
          <a href=${this.activeUrl}>
            <slot name="primary-image" ${ref(this.$primaryImage)}></slot>
          </a>
        </div>
        ${this.secondaryImage
        ? html`
            <div class="product-card__image product-card__image--secondary">
              <a href=${this.activeUrl}>
                <slot name="secondary-image"></slot>
              </a>
            </div>`
        : nothing
      }
        ${this.availableSwatches.length > 0
        ? html`
            <img ${ref(this.$swatchHoverImage)} class="product-card__swatch-hover-image" src=""/>
            `
        : nothing
      }

        ${this.renderBadges()}

        ${this.isHovered && this.mode !== 'predictive-search' ? this.renderQuickShopCTAs() : nothing}
      </div>
    `
  }

  renderBadges() {
    if (!this.badges) return null

    return html`
 <div class="badges">
        <span class="badges__item caption ${this.badges[0].toLowerCase().includes('sale') ? 'red-badge' : ''}">
          ${this.badges[0]}
        </span>
      </div>
    `
  }

  renderSwatch = (swatch) => {
    const swatchSanitize = this.sanitizeString(swatch)
    const variant = this.findVariantBySwatch(swatch)
    const optionUnavailable = this.checkColorSwatchUnavailable(swatch)

    const stateClasses = {
      'product-card__swatch--active': variant && variant.id === this.activeId,
      'product-card__swatch--sold-out': variant && optionUnavailable,
    }

    return html`
      <button
        class="product-card__swatch ${classMap(stateClasses)}"
        style="background: ${__NOVA_GLOBAL__.swatches[swatchSanitize]};"
        data-variant-id=${variant?.id}
        data-variant-image=${variant?.featured_image?.src}
        aria-label=${swatch}
        @click=${this.handleSwatchClick}
        @mouseenter=${this.hoverEnterSwatchShowVariantImage}
        @mouseleave=${this.hoverLeaveSwatchHideVariantImage}>
      </button>
    `
  }

  hoverEnterSwatchShowVariantImage(event) {
    const swatchHoverImage = this.$swatchHoverImage.value
    if (
      !swatchHoverImage ||
      event.target.getAttribute('data-variant-image') === null ||
      event.target.getAttribute('data-variant-image') === ''
    )
      return
    swatchHoverImage.src = event.target.getAttribute('data-variant-image')
    if (!swatchHoverImage.classList.contains('swatch-hover-image__show'))
      swatchHoverImage.classList.add('swatch-hover-image__show')
  }

  hoverLeaveSwatchHideVariantImage() {
    const swatchHoverImage = this.$swatchHoverImage.value
    if (!swatchHoverImage) return
    if (swatchHoverImage.classList.contains('swatch-hover-image__show'))
      swatchHoverImage.classList.remove('swatch-hover-image__show')
  }

  renderProductSwatches() {
    if (!this.availableSwatches.length) return nothing

    const shown = this.getSwatchesForDisplay()
    const carouselSwatches =
      this.mode === 'predictive-search' ? shown : this.availableSwatches

    const useCarousel =
      !this.isMobile &&
      this.slidesForCarousel !== 1 &&
      carouselSwatches.length > this.slidesForCarousel

    const remainingAfterCap = this.availableSwatches.length - shown.length
    const legacyMobileMore =
      this.mode !== 'predictive-search' &&
      this.availableSwatches.length > 3
    const predictiveMore =
      this.mode === 'predictive-search' && remainingAfterCap > 0

    return html`
      <div class="product-card__info">
        <sl-resize-observer @sl-resize=${this.handleResize}>
          <div class="product-card__swatch-container ${classMap({
      'product-card__swatch-container--carousel': useCarousel,
      'product-card__swatch-container--predictive':
        this.mode === 'predictive-search',
    })}">
            ${!useCarousel
        ? html`
                ${shown.map(this.renderSwatch)}

                ${legacyMobileMore
            ? html`<span class="product-card__swatch-container__trigger"><a href=${this.url}>+ ${this.availableSwatches.length - 3} more</a></span>`
            : predictiveMore
              ? html`<span class="product-card__swatch-container__trigger"><a href=${this.url}>+ ${remainingAfterCap} more</a></span>`
              : ''
          }
              `
        : html`
                <sl-carousel
                  navigation
                  slides-per-page=${this.slidesForCarousel}
                  slides-per-move=${this.slidesForCarousel}
                  style="--aspect-ratio: auto; --slide-gap: 10px;">
                  ${carouselSwatches.map(
          (swatch) => html`
                      <sl-carousel-item> ${this.renderSwatch(swatch)}</sl-carousel-item>
                    `,
        )}
                </sl-carousel>
              `
      }
          </div>
        </sl-resize-observer>
      </div>
    `
  }

  renderQuickShopCTAs() {
    const { addToBag, soldOut, selectOptions } = window.variantStrings

    if (this.variants.length === 1) {
      return html`
        <sl-button
          variant="primary"
          class="product-card__atc"
          ?disabled=${this.variants[0].available !== true}
          data-variant-id=${this.variants[0].id}
          ?loading=${this.status === quickShopState.loading}
          @click=${this.handleAddToCartClick}>
          ${this.variants[0].available ? addToBag : soldOut}
        </sl-button>
      `
    }

    return html`
      <sl-button
        variant="primary"
        class="product-card__atc product-card__atc--variants"
        href=${this.url}>
        ${selectOptions || 'Select Options'}
      </sl-button>
    `
  }

  render() {
    return html`
      <sl-card
        id="product-card-${this.id}"
        class="product-card"
        style="--padding: 0; --border-width: 0; --sl-shadow-x-small: 0;"
        @mouseenter=${this.handleMouseEnter}
        @mouseleave=${this.handleMouseLeave}
        @option-selected=${this.handleOptionSelection}>

        ${this.renderMedia()}

        <div class="product-card__content
          ${classMap({ 'mode__predictive-search': this.mode === 'predictive-search' })}"
          ${ref(this.$productCardContentRef)}>
          <header class="product-card__header flex flex-column flex-space-between">
        ${this.mode !== 'predictive-search' ? html`<span class="product-card__vendor caption block"> ${this.vendor}</span>` : nothing}
            <a href=${this.activeUrl}>
              <h2 class="product-card__title uppercase">
                ${this.title}
              </h2>
            </a>
            ${this.mode === 'predictive-search'
        ? html`
              <div
                class="product-card__summary product-card__summary--predictive"
              >
                ${this.renderProductSwatches()}
              </div>
            `
        : html`
              <div class="product-card__summary flex flex-space-between product-card__vendor">
                ${this.sku ? html`<span class="caption product-card__part"><span class="text-nowrap product-card__mpn">MPN#:</span> ${this.sku}</span>` : nothing}
                ${this.renderProductSwatches()}
              </div>
            `
      }
						<div class="flex flex-space-between product-card__subtitle">
							<nova-product-card-price
								class="product-card__price"
								.price=${this.activePrice}
								.compareAtPrice=${this.activeCompareAtPrice}
                .savings=${this.priceSaving}
              >
							</nova-product-card-price>
            </div>
          </header>


        </div>
      </sl-card>
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.helpers,
    styles.typography,
    styles.skeleton,
    css`
      :host {
        display: flex;
        flex-direction: column;
        position: relative;
        container-type: inline-size;
        width: 100%;
        position: relative;
        height: 100%;
      }

      :host a:focus-visible
      :host a:focus-visible img {
        border: 2px solid var(--nova-utility-focus);
      }

      .product-card,
      sl-card::part(base) {
        height: 100%;
        border-radius:12px;
        background-color: var(--nova-brand-reverse);

      }

      sl-card::part(body) {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      :host-context(.product-recommendations) {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        height: auto;
      }

      :host-context(.product-recommendations) .product-card,
      :host-context(.product-recommendations) sl-card::part(base),
      :host-context(.product-recommendations) sl-card::part(body) {
        width: 100%;
        min-width: 0;
        height: auto;
      }

      :host-context(.product-recommendations) .product-card__media {
        flex: 0 0 auto;
        width: 100%;
      }

      :host-context(.product-recommendations) .product-card__image {
        display: block;
        width: 100%;
        aspect-ratio: 1;
        overflow: hidden;
      }

      :host-context(.product-recommendations) .product-card__image a {
        display: block;
        width: 100%;
        height: 100%;
        position: relative;
      }

      :host-context(.product-recommendations) .product-card__image .img {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
        aspect-ratio: auto;
        overflow: hidden;
      }

      :host-context(.product-recommendations) .product-card__image .img img,
      :host-context(.product-recommendations) .product-card__image img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        max-width: none;
        max-inline-size: none;
        block-size: 100%;
        aspect-ratio: auto;
        object-fit: cover;
        object-position: center;
      }

      :host-context(.product-recommendations) .product-card__header {
        height: auto;
      }

      .product-card__content {
        padding: var(--nova-space-sm);
        border-radius: 0px 0px 12px 12px;
        border-top: 2px solid rgb(237, 237, 237);
        background: rgb(247 247 247);
        box-shadow: 1px 1px 10px #dadada;
        display: flex;
        flex-direction: column;
        flex: 1;
      }

      .product-card__header {
        display: flex;
        justify-content: flex-start;
        flex-direction: column;
      }

      .product-card__summary {
        gap: 10px;
        flex-direction: column;
        margin-bottom: var(--nova-space-xs);
      }

      .text-nowrap {
        white-space: nowrap;
        color: #353c3a;
      }

      .product-card__vendor {
          font-weight: 600;
          color: rgb(194 194 194);
          padding-bottom: 6px;
          font-size: 12px;
          text-transform: uppercase;
          display: flex;
      }
      
      .product-card__mpn , .product-card__part {
          font-weight: 500;
          font-size:12px;
          color: #b1b1b1;
          text-transform: uppercase;
      }    

     .product-card__title {
      font-size: var(--product-title-font-size-mobile, 14px);
      line-height: 1.15;
      flex: 1;
      font-weight: 800;
      letter-spacing: -0.01em;
      color: #424242;
     }

     @media screen and (min-width: 990px) {
      .product-card__title {
        font-size: var(--product-title-font-size-desktop, 16px);
      }
     }

      .mode__predictive-search .product-card__title {
        margin-bottom: var(--nova-space-2xs);
      }

      @media screen and (min-width: 768px) {
        .mode__predictive-search .product-card__header {
          flex-direction: column;
        }

        .mode__predictive-search .product-card__title {
          line-height: 125%;
          font-weight: 500;
          margin-bottom: var(--nova-space-2xs);
        }

        .mode__predictive-search .product-card__title.uppercase {
          font-size: 14px;
        }
      }

      .mode__predictive-search .product-card__title.uppercase {
        text-transform: none;
        font-size: 13px;
        line-height: 1.25;
        font-weight: 600;
        letter-spacing: 0;
      }

      .mode__predictive-search .product-card__media {
        max-height: 148px;
        overflow: hidden;
        border-radius: 8px 8px 0 0;
      }

      .mode__predictive-search .product-card__image {
        border-radius: 8px 8px 0 0;
      }

      .mode__predictive-search .product-card__image img {
        border-radius: 8px 8px 0 0;
        max-height: 148px;
        object-fit: contain;
      }

      .mode__predictive-search .product-card__content {
        padding: var(--nova-space-xs);
        border-radius: 0 0 8px 8px;
        box-shadow: none;
        border-top: 1px solid rgb(237, 237, 237);
      }

      .mode__predictive-search .product-card__summary--predictive {
        margin: var(--nova-space-2xs) 0;
      }

      .mode__predictive-search .product-card__swatch-container--predictive {
        flex-wrap: wrap;
        height: auto;
        min-height: 0;
        width: 100%;
        padding: 2px 0;
      }

      .mode__predictive-search
        .product-card__swatch-container--predictive
        .product-card__swatch:nth-child(n + 4) {
        display: inline-block;
      }

      .mode__predictive-search .product-card__swatch {
        width: 18px;
        height: 18px;
        max-width: 18px;
      }

      .mode__predictive-search .product-card__swatch--active::before {
        width: 22px;
        height: 22px;
      }

      .mode__predictive-search .product-card__price {
        font-size: 13px;
      }

      .product-card__media {
        position: relative;
      }

      .product-card .badges {
        position: absolute;
        top: var(--nova-space-xs);
        left: var(--nova-space-xs);
        flex-direction: column;
        align-items: flex-start;
        z-index: 6;
      }
      .product-card .badges span.badges__item.caption.red-badge {
          background: var(--badge-sale-bg, #eb0a1e) !important;
          color: var(--badge-sale-fg, #fff);
      }
      nova-product-card-quick-shop {
        display: block;
        position: absolute;
        bottom: var(--nova-space-xs);
        left: var(--nova-space-xs);
        right: var(--nova-space-sm);
      }

      .product-card:hover nova-product-card-quick-shop {
        display: block;
      }

      .product-card__image {
        width: 100%;
        aspect-ratio: 1;
        background: linear-gradient(150.38deg, rgb(222, 222, 218) 3.74%, rgba(222, 222, 218, 0.49) 63.73%, rgba(222, 222, 218, 0) 85.34%);
        transition: opacity 0.3s ease-in-out;
        border-radius:12px 12px 0px 0px;
      }

      .product-card__image img {
        display: block;
        width: 100%;
        height: auto;
        aspect-ratio: 1;
        object-fit: cover;
        object-position: center;
        border-radius: 12px 12px 0 0;
        
      }

      .product-card__image--primary {
        opacity: 1;
      }

      .product-card__image--secondary {
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
      }

      @container (min-width: 300px) {
        .product-card__media--enable-hover:hover .product-card__image--primary {
          opacity: 0;
          position: absolute;
          top: 0;
          left: 0;
        }

        .product-card__media--enable-hover:hover .product-card__image--secondary {
          opacity: 1;
          position: relative;
        }
      }

      .product-card__header {
        height: 100%;
      }

      .product-card__price {
        line-height: 130%;
        font-weight: 800;
        font-size: var(--product-price-font-size-mobile, 14px);
        font-family: 'Rubik';
        color: #eb0a1e !important;
        flex: 0;
      }

      @media screen and (min-width: 990px) {
        .product-card__price {
          font-size: var(--product-price-font-size-desktop, 14px);
        }
      }

      .product-card__price dl { margin: 0; }

      .product-card__price .price__sale {
        display: flex;
      }

      .product-card__price .price__compare {
        margin-left: 2px;
        color: var(--nova-brand-subdued);
      }

      .product-card__price dd {
        margin: 0;
      }

      @media screen and (min-width: 768px) {
        .mode__predictive-search .product-card__info {
          margin-top: 0;
        }
      }

      .product-card__info::-webkit-scrollbar {
        display: none;
      }

      @container (min-width: 300px) {
        .product-card__info { padding: 0 }
      }

      .product-card__swatch-container {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        height: 41px;
        width: fit-content;
        padding: 3px;
      }

      .product-card__swatch-container--carousel {
        width: 100%;
        padding: 0;
      }

      .product-card__swatch {
        width: var(--nova-product-color-swatch-size);
        height: var(--nova-product-color-swatch-size);
        max-width: var(--nova-product-color-swatch-size);
        border-radius: 50%;
        text-indent: -9999px;
        position: relative;
        padding: 0;
      }

      .product-card__swatch[style="background: #FFFFFF;"],
      .product-card__swatch[style="background: #ffffff;"] {
        border: 1px solid var(--nova-brand-neutral);
      }

      .product-card__swatch + .product-card__swatch {
        margin-left: var(--nova-space-xs);
      }

      .product-card__swatch--active::before {
        content: '';
        box-shadow: 0 0 0 1px black;
        position: absolute;
        border-radius: 50%;
        width: var(--nova-product-color-swatch-size-active);
        height: var(--nova-product-color-swatch-size-active);
        transform: translate(-2px, -2px);
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
      }

      .product-card__swatch--active[style="background: #FFFFFF;"]::before,
      .product-card__swatch--active[style="background: #ffffff;"]::before {
        transform: translate(-3px, -3px);
      }

      .product-card__swatch--sold-out::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, transparent 0%, transparent calc(50% - 2px), var(--nova-brand-reverse) calc(50% - 2px), var(--nova-brand-reverse) calc(50% - 1px), var(--nova-color-foreground) calc(50% - 1px), var(--nova-color-foreground) calc(50% + 1px), var(--nova-brand-reverse) calc(50% + 1px), var(--nova-brand-reverse) calc(50% + 2px), transparent calc(50% + 2px), transparent 100%);
        pointer-events: none;
        border-radius: 50%;
      }

      .product-card__swatch--active[style="background: #FFFFFF;"]::after,
      .product-card__swatch--active[style="background: #ffffff;"]::after {
        top: -1px;
        left: -1px;
        right: -1px;
        bottom: -1px;
      }

      .product-card__swatch:nth-child(n+4) {
        display: none;
      }

      .product-card__swatch-container__trigger {
        margin-left: 5px;
      }

      // hack for shoelace and active state on first element
      sl-carousel::part(scroll-container) {
        padding-left: 1px;
      }

      sl-carousel-item {
        width: calc(var(--nova-product-color-swatch-size-active) + 1px);
        height: var(--nova-product-color-swatch-size);
      }

      sl-carousel-item button {
        width: var(--nova-product-color-swatch-size);
      }

      @media screen and (min-width: 768px) {
        .product-card__swatch:nth-child(n+4) {
          display: inline-block;
        }

        .product-card__swatch-container__trigger {
          display: none;
        }

        .product-card__swatch--sold-out::after {
          top: 1px;
          left: 1px;
          right: 1px;
          bottom: 1px;
        }

        .product-card__content {
          padding: var(--nova-space-sm) var(--nova-space-sm) var(--nova-space-md);
        }
      }

      sl-carousel {
        width: 100%;
        height: 100%;
      }

      sl-carousel::part(base) {
        grid-template-rows: 1fr;
        grid-template-areas: '. slides .';
        gap: 3px;
      }

      sl-carousel::part(navigation-button) {
        padding: 0;
      }

      sl-carousel::part(scroll-container) {
        justify-items: flex-start;
      }

      .product-card__wishlist {
        position: absolute;
        right: var(--nova-space-xs);
        top: var(--nova-space-sm);
      }

      .product-card__wishlist svg {
        pointer-events: none;
      }

      .product-card__wishlist:hover svg {
        fill: var(--nova-brand-secondary);
      }

      .product-card__wishlist:active svg {
        fill: var(--nova-brand-ink);
      }

      .badges {
        display: flex;
        gap: var(--nova-space-2xs);
      }

      .badges__item {
        align-items: flex-start;
        color: var(--badge-default-fg, #000000);
        display: flex;
        font: var(--nova-body-utility);
        background-color: var(--badge-default-bg, #ABC4CA);
        border-radius: var(--nova-border-radius-s);
        letter-spacing: 1.4px;
        padding: var(--nova-space-xs, 10px) var(--nova-space-xs, 12px);
        text-transform: uppercase;
        pointer-events: none;
      }

      sl-button[variant='primary'] {
        transition: 0.15s;
        text-transform: uppercase;
      }

      sl-button[variant='primary']::part(base) {
        transition: 0.15s;
        background-color: var(--nova-brand-primary);
        border: 1px solid var(--nova-brand-primary);
        color: var(--nova-brand-reverse);
        border-radius: var(--nova-border-radius-l) !important;
        padding: var(--nova-space-xs) var(--nova-space-md);
        letter-spacing: 1.4px;
        font: 700 14px/40px var(--sl-font-sans);
        text-transform: uppercase;
        min-height: unset;
        height: 55px;
      }

      sl-button[variant='primary']::part(base):hover {
        color: var(--nova-brand-ink);
        background-color: var(--nova-color-background-secondary);
        border: 1px solid var(--nova-brand-ink);
      }

      sl-button[disabled]::part(base):hover {
        background-color: var(--nova-brand-neutral);
      }

      sl-button[variant='primary']::part(label) {
        display: flex;
        align-items: center;
      }

      .product-card__atc {
        position: absolute;
        bottom: var(--nova-space-xs);
        left: var(--nova-space-xs);
        right: var(--nova-space-xs);
      }

      .product-card__atc span {
        line-height: 40px;
      }

      .product-card__atc--confirm {
        width: 100%;
        height: 40px;
        display: flex;
        justify-content: center;
        background-color: white;
        border-radius: var(--nova-button-radius);
        background-color: var(--nova-brand-secondary);
      }

      .product-card__atc--error {
        background-color: var(--nova-utility-error);
      }

      .product-card__subtitle {
        flex-direction: column;
        gap: var(--nova-space-xs);
      }

      .product-card__price {
        order: 2;
      }

      @media screen and (min-width: 1200px) {
        .product-card__subtitle {
          flex-direction: row;
        }

        .product-card__price {
          order: 1;
        }
      }

      .product-card__swatch-hover-image {
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
        aspect-ratio: 1;
        object-fit: cover;
        object-position: center;
        opacity: 0;
        z-index: -1;
      }

      @media screen and (min-width: 768px) {
        .product-card__swatch-hover-image.swatch-hover-image__show {
          opacity: 1;
          z-index: 0;
        }
      }
    `,
  ]
}

customElements.define('nova-product-card', NovaProductCard)
